import React, { useEffect, useState } from 'react';
import moment from 'moment';
import {
  Button, Card, FormGroup, Input,
} from 'reactstrap';
import { useMutation } from '@apollo/react-hooks';
import { DELETE_TEMPLATE } from '../../../mutation/mutation';
import { GET_TEMPLATES } from '../../../query/query';
import { fontIsDeprecated, fontIsUploadedInApp, getTemplateFonts } from '../utils';
import Tags from '../tags/tags';
import { TemplateCardButtons } from '../categories-components/TemplateCard';
import TemplateStatus from './TemplateStatus';
import TemplateDatePickers from './TemplateDatePickers';
import RankPicker from './RankPicker';

const TemplateImage = ({ imageSrc }) => (
  <div style={styles.imageContainer}>
    <img alt="template" src={imageSrc} style={styles.image} />
  </div>
);

const Template = ({
  index,
  subCategoriesList,
  template: {
    _id,
    elements = [],
    name,
    rank,
    status,
    subCategories,
    thumbnailEn,
    thumbnailFr,
    premium,
    releaseDate: initialReleaseDate,
    internalNote,
  } = {},
  updateTemplate,
}) => {
  const [releaseDate, setReleaseDate] = useState(initialReleaseDate);
  const [texts, setTexts] = useState(elements);

  useEffect(() => {
    setReleaseDate(initialReleaseDate !== null ? moment(initialReleaseDate, 'YYYY-MM-DD').toDate() : undefined);
    setTexts(elements.filter(({ type } = {}) => type === 'text'));
  }, [elements, initialReleaseDate, subCategories]);

  const validateTemplate = () => {
    updateTemplate({ variables: { templateId: _id, updatedTemplate: { status: 'ACCEPTED', premium } } });
  };

  const rejectTemplate = () => {
    updateTemplate({ variables: { templateId: _id, updatedTemplate: { status: 'REJECTED' } } });
  };

  const setTemplateReleaseDate = (date) => {
    let dateStr;
    setReleaseDate(date === null ? undefined : date);

    if (date !== null) {
      dateStr = moment(date).format('YYYY-MM-DD');
    } else {
      dateStr = null;
    }
    updateTemplate({
      variables: {
        templateId: _id,
        updatedTemplate: { releaseDate: dateStr },
      },
    });
  };

  const updateTemplateSubCategories = (newSubCategories) => {
    updateTemplate({
      variables: {
        templateId: _id,
        updatedTemplate: {
          subCategories: newSubCategories.map(({ _id: categoriesId }) => categoriesId),
        },
      },
    });
  };

  const updateTemplatePremium = (newPremiumValue) => {
    const newSubCategories = subCategories || [];
    updateTemplate({
      variables: {
        templateId: _id,
        updatedTemplate: {
          premium: newPremiumValue,
          subCategories: newSubCategories.map(({ _id: categoriesId }) => categoriesId),
        },
      },
    });
  };

  const [deleteTemplateMutation] = useMutation(DELETE_TEMPLATE, {
    update: (cache, { data: { savDeleteTemplate } }) => {
      if (savDeleteTemplate) {
        const { savGetTemplates: savGetTemplatesTmp } = cache.readQuery({ query: GET_TEMPLATES });
        const savGetTemplatesNew = savGetTemplatesTmp.filter(({ _id: id }) => (_id !== id));
        cache.writeQuery({
          query: GET_TEMPLATES,
          data: { savGetTemplates: [...savGetTemplatesNew] },
        });
      }
    },
  });

  const deleteTemplateConfirm = () => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (confirm('Are you sure you want to permanently delete this template from the database? This can\'t be undone.')) {
      deleteTemplateMutation({
        variables: {
          templateId: _id,
        },
      });
    }
  };

  const updateTemplateRank = (newRank) => {
    updateTemplate({
      variables: {
        templateId: _id,
        updatedTemplate: {
          rank: +newRank,
        },
      },
    });
  };

  const [editedInternalNote, setEditedInternalNote] = useState(internalNote);
  const [lastSavedInternalNote, setLastSavedInternalNote] = useState(internalNote);

  const saveInternalNote = async () => {
    await updateTemplate({
      variables: {
        templateId: _id, updatedTemplate: { internalNote: editedInternalNote },
      },
    });
    setLastSavedInternalNote(editedInternalNote);
  };

  return (
    <Card key={_id} style={styles.card(index)}>
      <div style={styles.row}>
        <h3>{name}</h3>
      </div>
      <div style={styles.row}>
        <TemplateImage imageSrc={thumbnailFr} />
        <TemplateImage imageSrc={thumbnailEn} />
      </div>
      <div style={styles.row}>
        {(!releaseDate || moment(releaseDate).isSameOrBefore(moment())) && (
          <TemplateStatus
            status={status}
            validateTemplate={validateTemplate}
            rejectTemplate={rejectTemplate}
          />
        )}
      </div>
      <div style={styles.row}>
        <TemplateDatePickers
          status={status}
          releaseDate={releaseDate}
          setTemplateReleaseDate={setTemplateReleaseDate}
        />
      </div>
      <div style={styles.row}>
        <TemplateCardButtons
          onTogglePremium={(newPremiumValue) => updateTemplatePremium(newPremiumValue)}
          isPremium={premium}
        />
      </div>
      <div style={styles.row}>
        <Tags
          subCategories={subCategories}
          subCategoriesList={subCategoriesList}
          updateTemplateSubCategories={updateTemplateSubCategories}
        />
      </div>
      <div style={styles.row}>
        <RankPicker
          rank={rank}
          setTemplateRank={updateTemplateRank}
        />
      </div>
      <div style={styles.row}>
        <p>Fonts:</p>
        <ul>
          {getTemplateFonts(texts).map((font) => (
            <li key={font} style={styles.font(font)}>
              {fontIsUploadedInApp(font) && !fontIsDeprecated(font) && '✅ '}
              {fontIsUploadedInApp(font) && fontIsDeprecated(font) && '⚠️⚠️⚠️ DEPRECATED ⚠️⚠️⚠️ '}
              {!fontIsUploadedInApp(font) && '❌❌❌ MISSING ❌❌❌ '}
              {font}
            </li>
          ))}
        </ul>
      </div>
      <div style={styles.row}>
        <FormGroup>
          <Input
            type="textarea"
            name="internal_note"
            value={editedInternalNote}
            onChange={(evt) => setEditedInternalNote(evt.target.value)}
            placeholder="Internal Note"
            cols={50}
            rows={(editedInternalNote && editedInternalNote.length) > 0 ? 5 : 1}
          />
        </FormGroup>
      </div>
      {lastSavedInternalNote !== editedInternalNote && (
        <div style={styles.deletePermanentlyButtonContainer}>
          <Button
            type="button"
            color="primary"
            onClick={saveInternalNote}
            style={{ marginBottom: 15 }}
          >
            Save Internal Note
          </Button>
        </div>
      )}
      <div style={styles.row}>
        <Button
          type="button"
          color="primary"
          href={`/templates/create/${_id}`}
        >
          Modify template
        </Button>
        <Button
          type="button"
          color="primary"
          href={`/templates/create/${_id}/duplicate`}
          style={{
            marginLeft: 20,
          }}
        >
          Duplicate template
        </Button>
      </div>
      <div style={styles.deletePermanentlyButtonContainer}>
        <Button
          type="button"
          color="danger"
          onClick={deleteTemplateConfirm}
        >
          Delete Permanently
        </Button>
      </div>
    </Card>
  );
};

const canvasWidth = 320;
const canvasHeight = 240;
const styles = {
  card: (index) => ({
    backgroundColor: ['white', '#ededed'][index % 2],
    padding: 20,
    borderRadius: 30,
    marginBottom: 10,
    width: 800,
  }),
  font: (fontFamily) => ({ color: fontIsUploadedInApp(fontFamily) ? 'black' : 'red' }),
  image: {
    height: canvasHeight,
    width: canvasWidth,
  },
  deletePermanentlyButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageContainer: {
    height: canvasHeight,
    width: canvasWidth,
    marginBottom: 30,
    marginLeft: 30,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
  },
  note: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 10,
    color: '#66666',
    fontSize: 10,
  },
  status: (status) => {
    let color = 'black';
    if (status === 'ACCEPTED') color = 'green';
    if (status === 'REJECTED') color = 'red';
    return {
      color,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: 'unset',
      marginRight: 10,
    };
  },
  textBorders: ({
    x, y, height, width,
  }) => ({
    position: 'relative',
    top: y - (height / 2) - canvasHeight,
    left: x - (width / 2),
    height,
    width,
    border: '6px solid #8A64FF',
  }),
};

export default Template;



// WEBPACK FOOTER //
// ./src/components/templates-manager/template-components/template.js